import React, { useEffect, useState } from "react";
import LoadWizardHeader from "../../../components/loads/LoadWizardHeader";
import LoadWizardStepper from "../../../components/loads/LoadWizardStepper";
import StepOne from "./StepOne";
import StepTow from "./StepTwo";
import StepThree from "./StepThree";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadsResource } from "../../../helpers/configRoutes/backend_helper";
import { reset_load, update_load } from "../../../store/loads/action"; 

const hasPersistedId = (value) => Number.isInteger(Number(value)) && Number(value) > 0;

const normalizeDateTimeParts = (dateValue, timeValue = "") => {
    if (!dateValue) {
        return {
            date: "",
            time: timeValue || "",
        };
    }

    const parsed = new Date(dateValue);

    if (Number.isNaN(parsed.getTime())) {
        return {
            date: dateValue,
            time: timeValue || "",
        };
    }

    const yyyy = parsed.getFullYear();
    const mm = String(parsed.getMonth() + 1).padStart(2, "0");
    const dd = String(parsed.getDate()).padStart(2, "0");
    const hh = String(parsed.getHours()).padStart(2, "0");
    const min = String(parsed.getMinutes()).padStart(2, "0");
    const hasEmbeddedTime = String(dateValue).trim().length > 10;

    return {
        date: `${yyyy}-${mm}-${dd}`,
        time: hasEmbeddedTime ? `${hh}:${min}` : (timeValue || `${hh}:${min}`),
    };
};

const normalizeStopForEdit = (stop) => {
    const pickup = normalizeDateTimeParts(stop?.date, stop?.load_time);
    const delivery = normalizeDateTimeParts(stop?.delivery_date, stop?.delivery_time);

    return {
        ...stop,
        date: pickup.date,
        load_time: pickup.time,
        delivery_date: delivery.date,
        delivery_time: delivery.time,
    };
};

const normalizeLoadForEdit = (load) => ({
    ...load,
    stops: (load?.stops || []).map(normalizeStopForEdit),
});

const sanitizeStopForSave = (stop) => {
    const normalizedStop = { ...stop };

    if (!hasPersistedId(normalizedStop.id)) {
        delete normalizedStop.id;
    }

    return normalizedStop;
};

const sanitizeLoadForSave = (load) => ({
    ...load,
    stops: (load?.stops || []).map(sanitizeStopForSave),
    stops_deleted: (load?.stops_deleted || []).filter(hasPersistedId),
});

const baseLoadDraft = {
    id: null,
    charges: "",
    client_id: "",
    client_work_order: "",
    practical_miles: "",
    status_id: "",
    schedule_id: "",
    operator_id: "",
    truck_id: "",
    trailer_id: "",
    current_mileage_truck: "",
    current_mileage_trailer: "",
    truck_last_load: null,
    trailer_last_load: null,
    stops: [],
    stops_deleted: [],
    evidences: [],
    evidences_deleted: [],
    payments: [],
};

const normalizePrefillLoad = (load = {}) => ({
    ...baseLoadDraft,
    ...normalizeLoadForEdit(load),
    id: null,
    stops_deleted: [],
    evidences_deleted: [],
});

const AddUpdLoad = () => {
    const { id } = useParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const aiDraftContext = location.state?.aiDraftContext || null;
    const prefillLoad = location.state?.prefillLoad || null;

    const load = useSelector((state) => state.Load.load) || {};

    const [step, setStep] = useState(1);
    const [loadingSave, setLoadingSave] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    // Carga inicial: nuevo vs editar
    useEffect(() => {
        const init = async () => {
            if (id) {
                // Modo edición
                const response = await loadsResource("show", {}, id);
                if (response?.status && response.load) {
                    dispatch(update_load(normalizeLoadForEdit(response.load)));
                } else {
                    navigate("/loads");
                    return;
                }
            } else if (prefillLoad) {
                dispatch(update_load(normalizePrefillLoad(prefillLoad)));
            } else {
                // Modo nuevo
                dispatch(reset_load());
            }
            setInitialLoading(false);
        };

        init();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, prefillLoad]);

    // Status de pasos (derivado de load)
    const stepsStatus = {
        one: !!(
            load?.charges &&
            load?.client_id &&
            load?.status_id &&
            load?.schedule_id &&
            load?.operator_id &&
            load?.truck_id &&
            load?.trailer_id
        ),
        two: Array.isArray(load?.stops) && load.stops.length > 0,
        three: true,
    };

    // Ajustar el step según qué esté completo (cuando ya tenemos datos)
    useEffect(() => {
        if (initialLoading) return;

        if (!stepsStatus.one) {
            setStep(1);
        } else if (!stepsStatus.two) {
            setStep(2);
        } else if (!stepsStatus.three) {
            setStep(3);
        } else {
            setStep(3); // todo completo, lo dejamos en el último
        }
    }, [initialLoading, stepsStatus.one, stepsStatus.two, stepsStatus.three]);

    const steps = [
        {
            id: 1,
            label: "Load & equipment",
            desc: "Charges, client, truck, trailer and basic info.",
            isCompleted: stepsStatus.one,
        },
        {
            id: 2,
            label: "Stops & route",
            desc: "Pickup, delivery and intermediate stops.",
            isCompleted: stepsStatus.two,
            isBloked: !stepsStatus.one,
        },
        {
            id: 3,
            label: "Documents & evidence (optional)",
            desc: "Upload POD, rate confirmations and notes if available.",
            isCompleted: stepsStatus.three,
            isBloked: !stepsStatus.two,
        },
    ];

    const goNext = () =>
        setStep((prev) => (prev < steps.length ? prev + 1 : prev));
    const goBack = () =>
        setStep((prev) => (prev > 1 ? prev - 1 : prev));
    const isLastStep = step === steps.length;

    const onCancel = () => {
        navigate(aiDraftContext?.returnPath || "/loads");
    };

    const onSave = async () => {
        setLoadingSave(true);

        const payload = sanitizeLoadForSave(load);

        const response = await loadsResource(
            load.id ? "put" : "post",
            payload,
            load.id
        );

        if (response?.status) {
            dispatch(reset_load());
            navigate("/loads");
        }

        setLoadingSave(false);
    };

    const handleSetStep = (targetStep) => {
        // Bloqueo de navegación directa a pasos posteriores
        if (targetStep === 2 && !stepsStatus.one) return;
        if (targetStep === 3 && !stepsStatus.two) return;
        setStep(targetStep);
    };

    // Mientras carga el load al recargar / editar
    if (initialLoading) {
        return (
            <div className="min-h-screen bg-cs-surface flex items-center justify-center">
                <div className="flex flex-col items-center gap-2 text-sm text-cs-muted">
                    <span className="material-symbols-outlined animate-spin text-xl">
                        progress_activity
                    </span>
                    <span>Loading load data…</span>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cs-surface">
            <div className="px-2 md:px-3 py-3 max-w-auto mx-auto">
                {/* Header */}
                <LoadWizardHeader step={step} totalSteps={steps.length} />

                {/* Stepper */}
                <LoadWizardStepper
                    steps={steps}
                    step={step}
                    setStep={handleSetStep}
                />

                {aiDraftContext?.draftId && (
                    <div className="mb-4 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                            <div>
                                <p className="font-semibold">Creating load from AI draft #{aiDraftContext.draftId}</p>
                                <p className="text-xs text-blue-700">
                                    Review the draft using the same create-load flow before saving.
                                    {aiDraftContext.sourceDocumentName ? ` Source file: ${aiDraftContext.sourceDocumentName}.` : ""}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={onCancel}
                                className="inline-flex items-center justify-center rounded-xl border border-blue-200 bg-white px-3 py-2 text-xs font-semibold text-blue-700 transition hover:bg-blue-100"
                            >
                                Back to AI drafts
                            </button>
                        </div>
                    </div>
                )}

                {/* Contenido del paso */}
                <div className="bg-cs-bg border border-cs-border rounded-2xl shadow-sm px-4 md:px-6 py-5 md:py-6">
                    {step === 1 && (
                        <StepOne
                            step={step}
                            totalSteps={steps.length}
                            onBack={goBack}
                            onNext={goNext}
                            onCancel={onCancel}
                            isLastStep={isLastStep}
                        />
                    )}

                    {step === 2 && (
                        <StepTow
                            step={step}
                            totalSteps={steps.length}
                            onBack={goBack}
                            onNext={goNext}
                            onCancel={onCancel}
                            isLastStep={isLastStep}
                        />
                    )}

                    {step === 3 && (
                        <StepThree
                            step={step}
                            totalSteps={steps.length}
                            onBack={goBack}
                            onNext={goNext}
                            onSave={onSave}
                            onCancel={onCancel}
                            isLastStep={isLastStep}
                            loading={loadingSave}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default AddUpdLoad;
